import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { Card, Btn } from "../components/UI";
import { FInput, FSelect, FLabel } from "./engagement/FormControls";
import { ArrowUp, ArrowDown, Trash2, Plus, ShieldCheck, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";

const PALETTE = ["#B28704", "#E65100", "#C62828", "#AD1457", "#5E35B1", "#1565C0", "#00838F", "#2E7D32", "#8D6E63", "#37474F", "#F9A825", "#757575"];
const BEHAVIORS = [["open", "Open-like"], ["in_progress", "In-progress-like"], ["done", "Done-like"]];
const slug = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

export function TaskStatusManager() {
  const [rows, setRows] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [colorFor, setColorFor] = useState(null);
  const [draft, setDraft] = useState({ label: "", behavior: "open", color: PALETTE[5] });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/task-statuses").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!rows) return <div className="py-10 text-center text-sm text-[#84675B]">Loading…</div>;

  const mut = (fn) => { setRows((rs) => fn([...rs])); setDirty(true); };
  const patch = (key, p) => mut((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r)));
  const move = (i, dir) => mut((rs) => { const j = i + dir; if (j < 0 || j >= rs.length) return rs; [rs[i], rs[j]] = [rs[j], rs[i]]; return rs; });
  const removeRow = (key) => mut((rs) => rs.filter((r) => r.key !== key));
  const add = () => {
    const label = draft.label.trim();
    if (!label) return toast.error("Enter a status name");
    const key = slug(label);
    if (!key) return toast.error("Status name needs letters or numbers");
    if (rows.some((r) => r.key === key)) return toast.error("A status with that name already exists");
    mut((rs) => [...rs, { key, label, color: draft.color, behavior: draft.behavior, archived: false, protected: false }]);
    setDraft({ label: "", behavior: "open", color: PALETTE[5] });
  };
  const saveAll = async () => {
    setSaving(true);
    try {
      const r = await api.put("/task-statuses", { statuses: rows });
      setRows(r.data); setDirty(false);
      toast.success("Task statuses saved - applied across the Tasks board");
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="task-status-manager">
      <Card className="p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[240px]">
            <h3 className="font-heading text-base font-bold text-[#3E2723]">Task Statuses</h3>
            <p className="text-xs text-[#7B6157]">Add, rename, recolor, reorder, or archive the workflow states on the Tasks board. Done-like statuses are excluded from overdue and open-task counts. Completed and Archived are protected.</p>
          </div>
          <Btn data-testid="task-status-save-btn" onClick={saveAll} disabled={!dirty || saving}>{saving ? "Saving…" : dirty ? "Save Changes" : "Saved"}</Btn>
        </div>
      </Card>

      <Card className="divide-y divide-[#F1EEE7]">
        {rows.map((s, i) => (
          <div key={s.key} data-testid={`task-status-row-${s.key}`} className={`flex flex-wrap items-center gap-3 px-4 py-3 ${s.archived ? "opacity-50" : ""}`}>
            <div className="flex flex-col">
              <button data-testid={`task-status-up-${s.key}`} aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)} className="text-[#7B6157] hover:text-[#3E2723] disabled:opacity-20"><ArrowUp size={13} /></button>
              <button data-testid={`task-status-down-${s.key}`} aria-label="Move down" disabled={i === rows.length - 1} onClick={() => move(i, 1)} className="text-[#7B6157] hover:text-[#3E2723] disabled:opacity-20"><ArrowDown size={13} /></button>
            </div>
            <div className="relative">
              <button data-testid={`task-status-color-${s.key}`} onClick={() => setColorFor(colorFor === s.key ? null : s.key)}
                className="h-7 w-7 rounded-full border-2 border-white shadow ring-1 ring-[#E5DFD3]" style={{ backgroundColor: s.color }} title="Change color" />
              {colorFor === s.key && (
                <div className="absolute left-0 top-9 z-30 grid grid-cols-6 gap-1.5 rounded-xl border border-[#EFEBE0] bg-white p-2.5 shadow-xl" data-testid="task-status-palette">
                  {PALETTE.map((c) => (
                    <button key={c} data-testid={`palette-${c.slice(1)}`} aria-label={`Color ${c}`} onClick={() => { patch(s.key, { color: c }); setColorFor(null); }}
                      className={`h-6 w-6 rounded-full ${s.color === c ? "ring-2 ring-offset-1 ring-[#3E2723]" : ""}`} style={{ backgroundColor: c }} />
                  ))}
                </div>
              )}
            </div>
            <div className="w-56"><FInput data-testid={`task-status-label-${s.key}`} className="h-9" maxLength={40} value={s.label} onChange={(e) => patch(s.key, { label: e.target.value })} /></div>
            <div className="w-44"><FSelect data-testid={`task-status-behavior-${s.key}`} className="h-9" value={s.behavior} disabled={s.protected} onChange={(e) => patch(s.key, { behavior: e.target.value })}>
              {BEHAVIORS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </FSelect></div>
            <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ backgroundColor: `${s.color}1F`, color: s.color }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: s.color }} />{s.label || s.key}
            </span>
            <div className="ml-auto flex items-center gap-1.5">
              {s.protected ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#F5F2EB] px-2.5 py-1 text-[10px] font-bold text-[#7B6157]" data-testid={`task-status-protected-${s.key}`}><ShieldCheck size={11} /> PROTECTED</span>
              ) : (
                <>
                  <button data-testid={`task-status-archive-${s.key}`} title={s.archived ? "Restore" : "Archive (hides from pickers, existing tasks keep it)"}
                    onClick={() => patch(s.key, { archived: !s.archived })}
                    className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] hover:bg-[#EDE7DC] flex items-center justify-center">
                    {s.archived ? <ArchiveRestore size={13} /> : <Archive size={13} />}
                  </button>
                  <button data-testid={`task-status-delete-${s.key}`} title="Delete status" onClick={() => removeRow(s.key)}
                    className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] hover:bg-[#FFEBEE] hover:text-[#C62828] flex items-center justify-center"><Trash2 size={13} /></button>
                </>
              )}
            </div>
          </div>
        ))}
      </Card>

      <Card className="p-4">
        <FLabel>Add a status</FLabel>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <div className="w-56"><FInput data-testid="task-status-new-label" className="h-10" maxLength={40} placeholder="e.g. Waiting on Client" value={draft.label}
            onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} /></div>
          <div className="w-44"><FSelect data-testid="task-status-new-behavior" className="h-10" value={draft.behavior} onChange={(e) => setDraft((d) => ({ ...d, behavior: e.target.value }))}>
            {BEHAVIORS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </FSelect></div>
          <div className="flex items-center gap-1.5">
            {PALETTE.slice(0, 8).map((c) => (
              <button key={c} aria-label={`Pick color ${c}`} onClick={() => setDraft((d) => ({ ...d, color: c }))}
                className={`h-6 w-6 rounded-full ${draft.color === c ? "ring-2 ring-offset-1 ring-[#3E2723]" : ""}`} style={{ backgroundColor: c }} />
            ))}
          </div>
          <Btn data-testid="task-status-add-btn" variant="outline" className="rounded-lg" onClick={add}><Plus size={14} /> Add</Btn>
        </div>
        <p className="mt-2 text-[11px] text-[#84675B]">Changes apply only when you press Save Changes. Archiving hides a status from pickers; existing tasks keep it.</p>
      </Card>
    </div>
  );
}
